import { memo } from "react";
import { t } from "components/CentralDataMangement/translation/Translation";
import CustomNotify from "components/common/atoms/CustomNotify";
import type { LogEntry } from "./Interface";
import CloneIcon from "static/images/CloneIcon";
import TextEllipsis from "components/common/atoms/TextEllipsis";
import Accordion from "react-bootstrap/Accordion";

interface LogEntriesProps {
  logs: LogEntry[];
  viewMode: "table" | "raw" | "json";
}

const LogEntries = memo(({ logs, viewMode }: LogEntriesProps) => {
  const getLevelBadgeClass = (level: string) =>
    ({
      ERROR: "badge bg-danger",
      WARN: "badge bg-warning text-dark",
      INFO: "badge bg-info",
      DEBUG: "badge bg-secondary",
    }[level?.toUpperCase()] || "badge bg-primary");

  const handleCopyLog = async (log: LogEntry) => {
    const textToCopy = `[${log.timestamp}] ${log.level}: ${log.sanitizedMessage}`;

    try {
      // Check if clipboard API is supported and available
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        CustomNotify({
          type: "success",
          message: t("Log copied to clipboard!"),
        });
      } else {
        // Fallback for non-secure contexts or older browsers
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand("copy");
          CustomNotify({
            type: "success",
            message: t("Log copied to clipboard!"),
          });
        } catch (fallbackErr) {
          CustomNotify({
            type: "error",
            message: t("Could not copy log. Please copy manually."),
          });
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error("Copy failed:", err);
      CustomNotify({ type: "error", message: t("Could not copy log.") });
    }
  };

  if (viewMode === "raw") {
    return (
      <pre
        className="p-3 m-0 overflow-auto bg-dark text-light h-100"
        style={{ fontSize: "12px" }}
      >
        {logs
          .map(
            (log) => `[${log.timestamp}] ${log.level}: ${log.sanitizedMessage}`
          )
          .join("\n")}
      </pre>
    );
  }

  if (viewMode === "json") {
    return (
      <pre
        className="p-3 m-0 overflow-auto bg-light h-100"
        style={{ fontSize: "12px" }}
      >
        {JSON.stringify(logs, null, 2)}
      </pre>
    );
  }

  return (
    <div
      id="log-entries-content"
      className="scrollBarDesign log-entries-content-wrapper padding1"
    >
      {logs.length === 0 ? (
        <div className="text-center text-muted">
          <p>No log entries match your criteria.</p>
        </div>
      ) : (
        <>
          {logs.map((log, index) => (
            <div
              className="d-flex align-items-start w-100 mb-3 logEntryAccordionWrapper"
              key={`${log.line}-${index}`}
            >
              <Accordion
                defaultActiveKey={null}
                key={`log-${index}`}
                className="w-100 logEntryAccordion"
              >
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <div className="d-flex justify-content-between w-100 fw-bold">
                      <div className="d-flex align-items-center w-100">
                        <div style={{ width: "20%" }} className="text-break">
                          <small>
                            {new Date(log.timestamp).toLocaleString()}
                          </small>
                        </div>
                        <div style={{ width: "15%" }} className="text-break">
                          <span className={getLevelBadgeClass(log.level)}>
                            {log.level}
                          </span>
                        </div>
                        <div
                          style={{ width: "62%" }}
                          className="text-break text-end"
                        >
                          <span
                            className="table-action-icons cursor-pointer"
                            onClick={() => handleCopyLog(log)}
                            title={t("Copy")}
                          >
                            <CloneIcon />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="row">
                          <div className="col-12">
                            <div className="mb-2 text-break">
                              <strong>Message:</strong>
                              <div className="mt-1 p-2 bg-light rounded">
                                {log.sanitizedMessage}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          ))}
        </>
      )}
    </div>
  );
});

LogEntries.displayName = "LogEntries";

export default LogEntries;
